/**
 * PushOptIn — banner pro přihlášení k push notifikacím.
 *
 * Zobrazuje se ve spodní části stránky, pokud:
 *   - Prohlížeč podporuje Web Push
 *   - Povolení ještě nebylo rozhodnuto (default)
 *   - Uživatel ho ručně neodmítl (localStorage "bw:push:dismissed")
 *
 * Po kliknutí na "Povolit" zobrazí systémový dialog pro notifikace.
 */
import { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { usePushSubscription } from "../hooks/usePushSubscription.js";

const DISMISSED_KEY = "bw:push:dismissed";

interface PushOptInProps {
  /** JWT token pro přihlášení subscription k API. */
  token?: string;
}

export function PushOptIn({ token }: PushOptInProps) {
  const [visible, setVisible] = useState(false);
  // exactOptionalPropertyTypes: předáváme token jen pokud existuje
  const opts: Parameters<typeof usePushSubscription>[0] = {};
  if (token) opts.token = token;
  const { isSupported, permission, isSubscribed, isLoading, subscribe } = usePushSubscription(opts);

  useEffect(() => {
    if (
      isSupported &&
      permission === "default" &&
      !isSubscribed &&
      localStorage.getItem(DISMISSED_KEY) !== "1"
    ) {
      // Zobraz banner s 2s zpožděním (nestresuj hned při načtení)
      const timer = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(timer);
    }
    setVisible(false);
    return undefined;
  }, [isSupported, permission, isSubscribed]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
  };

  const handleSubscribe = async () => {
    // Banner schovej jen při úspěchu — jinak nech uživatele zkusit znovu
    // (subscribe nastaví `error`, který se může zobrazit/zalogovat).
    const ok = await subscribe();
    if (ok) setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Notifikace z Beer Wars"
      className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto"
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <Bell size={22} className="text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-foreground">Notifikace z Beer Wars</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Dostávej zprávy o duelech, výsledcích a novinkách přímo do telefonu — i když máš
              aplikaci zavřenou.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => void handleSubscribe()}
                disabled={isLoading}
                className="btn btn-sm px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {isLoading ? "Čekám…" : "Povolit notifikace"}
              </button>
              <button
                onClick={handleDismiss}
                className="btn btn-sm px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted transition-colors"
              >
                Ne, díky
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
